import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Zap, Users, Target } from 'lucide-react';

export const HeroSectionWithModalLegacy = ({ content, onPostProject }) => {
  const navigate = useNavigate();

  // This is the active homepage hero. Its copy is supplied by the public v1
  // homepage-content endpoint and edited from the existing Hero admin section.
  const heroContent = content || {
    title: 'Get The Best Home Repair Deals',
    subtitle: 'Through Competitive Bidding',
    description: 'Unlike traditional platforms, FixDirectly lets service providers compete for your project. Post your home repair needs and watch qualified professionals bid to win your business with their best prices and proposals.',
    cta_button: { text: 'Post Your Project', url: '/get-started' },
  };
  const cta = typeof heroContent.cta_button === 'string'
    ? { text: heroContent.cta_button, url: '/get-started' }
    : (heroContent.cta_button || {});
  // This legacy design has approved fixed marketing copy. The API still
  // provides the CTA configuration, while the visual message remains stable.
  const description = 'Unlike traditional platforms, FixDirectly lets service providers compete for your project. Post your home repair needs and watch qualified professionals bid to win your business with their best prices and proposals.';

  const handlePrimaryCta = () => {
    // `/get-started` is the legacy CMS default. Keep it mapped to the current
    // post-project flow instead of navigating to a route that no longer exists.
    if (!cta.url || cta.url === '/get-started' || cta.url === '#') {
      onPostProject?.();
      return;
    }

    if (/^https?:\/\//i.test(cta.url)) {
      window.location.assign(cta.url);
      return;
    }

    navigate(cta.url);
  };

  return (
    <>
      <section className="relative bg-gradient-to-br from-emerald-50 via-white to-teal-50 py-20 lg:py-28 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-32 h-32 bg-emerald-600 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-40 h-40 bg-teal-600 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-left">
              <div className="flex items-center space-x-2 mb-6">
                <div className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">
                  Revolutionary Approach
                </div>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-8 leading-[1.08]">
                <span className="block">Get The Best <span className="text-emerald-600">Home</span></span>
                <span className="block"><span className="text-emerald-600">Repair</span> Deals</span>
                <span className="block">Through <span className="text-teal-600">Competitive</span></span>
                <span className="block text-teal-600">Bidding</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-2xl">
                {description}
              </p>

              {/* Key Benefits */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <div className="flex items-center space-x-2 bg-white/70 backdrop-blur-sm px-4 py-2 rounded-lg border border-emerald-100">
                  <Target className="text-emerald-600" size={20} />
                  <span className="text-gray-700 font-medium">Better Prices</span>
                </div>
                <div className="flex items-center space-x-2 bg-white/70 backdrop-blur-sm px-4 py-2 rounded-lg border border-emerald-100">
                  <Users className="text-emerald-600" size={20} />
                  <span className="text-gray-700 font-medium">More Options</span>
                </div>
                <div className="flex items-center space-x-2 bg-white/70 backdrop-blur-sm px-4 py-2 rounded-lg border border-emerald-100">
                  <Zap className="text-emerald-600" size={20} />
                  <span className="text-gray-700 font-medium">Faster Results</span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={handlePrimaryCta}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 text-lg rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl group"
                >
                  {cta.text || 'Post Your Project'}
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-200" size={20} />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/signup?role=provider')}
                  className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-8 py-4 text-lg rounded-xl transition-all duration-200"
                >
                  Join as a Service Provider
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center space-x-6 mt-8 pt-8 border-t border-gray-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">2,500+</div>
                  <div className="text-sm text-gray-600">Projects Completed</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">1,200+</div>
                  <div className="text-sm text-gray-600">Service Providers</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">4.9★</div>
                  <div className="text-sm text-gray-600">Average Rating</div>
                </div>
              </div>
            </div>

            {/* Right Content - Illustration */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-8 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="space-y-4">
                  <div className="bg-emerald-50 rounded-lg p-4 border-l-4 border-emerald-500">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">Kitchen Renovation</span>
                      <span className="text-emerald-600 font-bold">5 Bids</span>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">Complete kitchen remodel with new cabinets and countertops</p>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center bg-white rounded p-2">
                        <span className="text-sm text-gray-700">ProBuilders Inc.</span>
                        <span className="font-bold text-emerald-600">$12,500</span>
                      </div>
                      <div className="flex justify-between items-center bg-white rounded p-2">
                        <span className="text-sm text-gray-700">Home Masters</span>
                        <span className="font-bold text-emerald-600">$11,800</span>
                      </div>
                      <div className="flex justify-between items-center bg-emerald-100 rounded p-2 border-2 border-emerald-300">
                        <span className="text-sm text-gray-700">Elite Renovations</span>
                        <span className="font-bold text-emerald-700">$10,999</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 bg-emerald-500 text-white p-3 rounded-full shadow-lg animate-bounce">
                <Target size={24} />
              </div>
              <div className="absolute -bottom-4 -left-4 bg-teal-500 text-white p-3 rounded-full shadow-lg animate-pulse">
                <Users size={24} />
              </div>
            </div>
          </div>
        </div>
      </section>

    </>
  );
};
